// Client-side compositing engine — a faithful TypeScript port of the old
// app's public/raster.js. All scene styles, change layers, sensitivity levels,
// radiometric normalization, and sub-pixel co-registration are computed here
// from raw band arrays fetched ONCE per polygon — no server round-trip when
// the user just changes how it's displayed.
//
// Display math (the part that keeps desert scenes from blowing out): each band
// is stretched between its own per-scene percentiles (true color 2–98, SWIR
// styles 1–99) and then gamma-corrected — never a fixed divide-by-constant.
// The `hdr` style adds a second, local stage on top of that global stretch
// (see LocalTone) for scenes whose shadows and highlights can't share one curve.

export interface RawScene {
  width: number;
  height: number;
  bands: Record<string, Uint16Array | Float32Array>;
}

export type BandFit = { gain: number; offset: number };
export type BandFits = Record<string, BandFit>;

export const RAW_BANDS = ['B02', 'B03', 'B04', 'B05', 'B06', 'B07', 'B08', 'B8A', 'B11', 'B12'];
export const SCALE = 10000; // must match server's RAW_SCALE

// Sentinel-1 SAR: VV/VH backscatter, plus a derived VV/VH ratio band computed
// at decode time (scan.ts) for the false-colour view. SAR styles slot into
// SCENE_STYLES below so the optical composite path renders them unchanged.
export const SAR_BANDS = ['VV', 'VH'];
export const SAR_STYLE_NAMES = new Set(['vv', 'vh', 'vvvh']);
export const isSarStyle = (name: string): boolean => SAR_STYLE_NAMES.has(name);

const stretchCache = new WeakMap<RawScene, Map<string, { lo: number; hi: number }>>();
const fitCache = new WeakMap<RawScene, WeakMap<RawScene, Map<string, BandFits>>>();
const toneCache = new WeakMap<RawScene, Map<string, ToneMap>>();

// Sample a band (skipping masked-out pixels) and return the value at two
// percentiles, for a punchy "auto contrast" stretch like EO Browser/USGS use —
// this is what gives composites their classic high-contrast look instead of
// the blown-out result of a flat linear gain.
function percentileStretch(band: ArrayLike<number>, mask: ArrayLike<number>,
                           loPct: number, hiPct: number, stride: number) {
  const samples: number[] = [];
  for (let i = 0; i < band.length; i += stride) {
    if (mask[i]! > SCALE / 2) samples.push(band[i]!);
  }
  if (samples.length < 20) return { lo: 0, hi: SCALE };
  samples.sort((a, b) => a - b);
  const lo = samples[Math.floor((loPct / 100) * (samples.length - 1))]!;
  const hi = samples[Math.floor((hiPct / 100) * (samples.length - 1))]!;
  return { lo, hi: Math.max(hi, lo + 1) };
}

function cachedPercentileStretch(raw: RawScene, bandName: string,
                                 loPct: number, hiPct: number, stride: number) {
  let cache = stretchCache.get(raw);
  if (!cache) {
    cache = new Map();
    stretchCache.set(raw, cache);
  }
  const key = `${bandName}|${loPct}|${hiPct}|${stride}`;
  let hit = cache.get(key);
  if (!hit) {
    hit = percentileStretch(raw.bands[bandName]!, raw.bands.dataMask!, loPct, hiPct, stride);
    cache.set(key, hit);
  }
  return hit;
}

function stretch(v: number, lo: number, hi: number, gamma: number): number {
  let t = (v - lo) / (hi - lo);
  if (t < 0) t = 0; else if (t > 1) t = 1;
  return gamma === 1 ? t : Math.pow(t, gamma);
}

// Local tone mapping — the "HDR" path. A percentile stretch fits ONE tone curve
// to the whole scene, so a frame holding both sunlit desert and a shadowed
// valley (or a hangar apron next to its own shadow) has to sacrifice one end to
// expose the other. CLAHE gives every tile its own curve derived from the local
// histogram, clips that histogram so featureless ground can't have its sensor
// noise stretched into mush, and interpolates between tile centres so no tile
// seams appear. Chroma is scaled by the same gain the luminance received, which
// preserves hue exactly while letting colour survive the much steeper curve.
export interface LocalTone {
  tiles: number;   // grid is tiles x tiles across the scene
  clip: number;    // clip limit, as a multiple of a perfectly flat histogram
  amount: number;  // 0-1 blend of the equalized luminance over the original
  sat: number;     // extra chroma multiplier on top of the luminance gain
}

export interface SceneStyle {
  channels: [string, string, string];
  gamma: number;
  lo: number;
  hi: number;
  local?: LocalTone;
}

// Scene composite styles. SWIR/CIR are far better than natural color for
// fire/burn/blast/rubble damage — both push disturbed ground and heat/char
// into a distinct, high-contrast channel instead of blending into gray.
export const SCENE_STYLES: Record<string, SceneStyle> = {
  truecolor: { channels: ['B04', 'B03', 'B02'], gamma: 1.0, lo: 2, hi: 98 },
  // Same three bands, locally tone-mapped. The global percentiles are pushed
  // out to 0.2/99.8 on purpose: this style deliberately keeps the highlights
  // and shadows a 2-98 stretch throws away, then recovers detail in both from
  // the per-tile curves instead. Shadow interiors, roofs and dust plumes read
  // in one frame, at the cost of a picture that is no longer photometric —
  // brightness here is local contrast, not reflectance, so read damage from the
  // change layers and use this for interpretation.
  hdr: { channels: ['B04', 'B03', 'B02'], gamma: 1.0, lo: 0.2, hi: 99.8,
         local: { tiles: 10, clip: 3, amount: 0.9, sat: 1.2 } },
  // Classic False Color Infrared (NIR->R, Red->G, Green->B): the "high
  // contrast, red-tinged" look from vegetation's strong NIR reflectance.
  colorir: { channels: ['B08', 'B04', 'B03'], gamma: 0.85, lo: 2, hi: 98 },
  // SWIR2/NIR-narrow/Red: active fire & fresh burns glow orange-red, healthy
  // vegetation green, water near-black.
  swir: { channels: ['B12', 'B8A', 'B04'], gamma: 0.85, lo: 1, hi: 99 },
  // SWIR2/SWIR1/NIR: char and exposed mineral surfaces separate cleanly from
  // moist vegetation without relying on the visible bands through smoke/haze.
  burnscar: { channels: ['B12', 'B11', 'B08'], gamma: 0.86, lo: 1, hi: 99 },
  // Smoke & fire separation: SWIR2 (penetrates smoke) -> R, Red -> G,
  // Blue (scattered strongly by smoke aerosols) -> B.
  smokefire: { channels: ['B12', 'B04', 'B02'], gamma: 0.9, lo: 1, hi: 99 },

  // --- Sentinel-1 SAR views. gamma < 1 lifts the dark speckle so structure
  // reads; the percentile stretch handles the wide backscatter range. ---
  vv: { channels: ['VV', 'VV', 'VV'], gamma: 0.7, lo: 2, hi: 98 },
  vh: { channels: ['VH', 'VH', 'VH'], gamma: 0.7, lo: 2, hi: 98 },
  // Classic S1 false colour: VV -> R (urban/rough bright), VH -> G (volume /
  // vegetation), VV/VH ratio -> B (separates smooth water & bare soil).
  vvvh: { channels: ['VV', 'VH', 'ratio'], gamma: 0.8, lo: 2, hi: 98 },
};

export type SceneStyleName = 'truecolor' | 'hdr' | 'colorir' | 'swir' | 'burnscar'
                           | 'smokefire' | 'ndvi' | 'nbr' | 'ndmi' | 'ndwi' | 'mndwi';

type Stretch = { lo: number; hi: number };

// Rec.709 luma. The tone curve is applied to luminance only, never per channel:
// equalizing R, G and B independently is what turns a hazy scene magenta.
const LUMA: [number, number, number] = [0.2126, 0.7152, 0.0722];
const TONE_BINS = 256;

export interface ToneMap {
  tilesX: number; tilesY: number;
  tileW: number; tileH: number;
  luts: Float32Array;      // tilesX * tilesY consecutive TONE_BINS-entry LUTs
}

function bandValue(bands: RawScene['bands'], name: string, i: number,
                   transforms: BandFits): number {
  const fit = transforms[name];
  return fit ? bands[name]![i]! * fit.gain + fit.offset : bands[name]![i]!;
}

// Stretched luminance in 0-1, plus the validity mask both passes share.
function sceneLuminance(raw: RawScene, spec: SceneStyle, stretches: Stretch[],
                        transforms: BandFits): { lum: Float32Array; valid: Uint8Array } {
  const { width, height, bands } = raw;
  const mask = bands.dataMask!;
  const lum = new Float32Array(width * height);
  const valid = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    if (mask[i]! <= SCALE / 2) continue;
    valid[i] = 1;
    let l = 0;
    for (let c = 0; c < 3; c++) {
      const { lo, hi } = stretches[c]!;
      l += LUMA[c]! * stretch(bandValue(bands, spec.channels[c]!, i, transforms),
                              lo, hi, spec.gamma);
    }
    lum[i] = l;
  }
  return { lum, valid };
}

// Contrast-limited histogram equalization, one LUT per tile. Clipped mass is
// redistributed uniformly, so each LUT still ends at 1 and the transform stays
// monotonic — a tile can redistribute contrast but never invert it.
function buildToneMap(lum: Float32Array, valid: Uint8Array, width: number, height: number,
                      local: LocalTone): ToneMap {
  // A tile's histogram is only worth trusting when it holds many more pixels
  // than it has bins, so a small AOI gets fewer, larger tiles rather than a
  // grid of curves each fitted to noise.
  const tilesX = Math.max(1, Math.min(local.tiles, Math.floor(width / 32)));
  const tilesY = Math.max(1, Math.min(local.tiles, Math.floor(height / 32)));
  const tileW = width / tilesX, tileH = height / tilesY;
  const luts = new Float32Array(tilesX * tilesY * TONE_BINS);
  const hist = new Float32Array(TONE_BINS);
  for (let ty = 0; ty < tilesY; ty++) {
    const y0 = Math.floor(ty * tileH), y1 = Math.floor((ty + 1) * tileH);
    for (let tx = 0; tx < tilesX; tx++) {
      const x0 = Math.floor(tx * tileW), x1 = Math.floor((tx + 1) * tileW);
      const base = (ty * tilesX + tx) * TONE_BINS;
      hist.fill(0);
      let count = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const i = y * width + x;
          if (!valid[i]) continue;
          let bin = Math.round(lum[i]! * (TONE_BINS - 1));
          if (bin < 0) bin = 0; else if (bin > TONE_BINS - 1) bin = TONE_BINS - 1;
          hist[bin]!++;
          count++;
        }
      }
      // A tile that is mostly cloud shadow, off-swath or nodata has no
      // trustworthy histogram — leave it on the identity curve so its
      // neighbours' interpolation carries it rather than inventing contrast.
      if (count < TONE_BINS) {
        for (let b = 0; b < TONE_BINS; b++) luts[base + b] = b / (TONE_BINS - 1);
        continue;
      }
      const limit = Math.max(1, local.clip * count / TONE_BINS);
      let excess = 0;
      for (let b = 0; b < TONE_BINS; b++) {
        if (hist[b]! > limit) { excess += hist[b]! - limit; hist[b] = limit; }
      }
      const spread = excess / TONE_BINS;
      let cdf = 0;
      for (let b = 0; b < TONE_BINS; b++) {
        cdf += hist[b]! + spread;
        luts[base + b] = cdf / count;
      }
    }
  }
  return { tilesX, tilesY, tileW, tileH, luts };
}

function lutAt(tone: ToneMap, tx: number, ty: number, v: number): number {
  const base = (ty * tone.tilesX + tx) * TONE_BINS;
  let p = v * (TONE_BINS - 1);
  if (p < 0) p = 0; else if (p > TONE_BINS - 1) p = TONE_BINS - 1;
  const b0 = Math.floor(p), b1 = Math.min(TONE_BINS - 1, b0 + 1);
  const f = p - b0;
  return tone.luts[base + b0]! * (1 - f) + tone.luts[base + b1]! * f;
}

// Bilinear blend of the four surrounding tile curves. Sampling one tile's LUT
// per pixel would leave visible tile edges; this is what makes CLAHE seamless.
function toneMapValue(tone: ToneMap, x: number, y: number, v: number): number {
  const clampX = (t: number) => Math.min(tone.tilesX - 1, Math.max(0, t));
  const clampY = (t: number) => Math.min(tone.tilesY - 1, Math.max(0, t));
  const gx = x / tone.tileW - 0.5, gy = y / tone.tileH - 0.5;
  const ix = Math.floor(gx), iy = Math.floor(gy);
  const fx = gx - ix, fy = gy - iy;
  const x0 = clampX(ix), x1 = clampX(ix + 1), y0 = clampY(iy), y1 = clampY(iy + 1);
  const top = lutAt(tone, x0, y0, v) * (1 - fx) + lutAt(tone, x1, y0, v) * fx;
  const bottom = lutAt(tone, x0, y1, v) * (1 - fx) + lutAt(tone, x1, y1, v) * fx;
  return top * (1 - fy) + bottom * fy;
}

// The tone map a scene generates from its OWN pixels, cached per style. Pair
// rendering deliberately does not use the after scene's own map — see
// compositeScenePair.
export function sceneToneMap(raw: RawScene, styleName: string): ToneMap | null {
  const spec = SCENE_STYLES[styleName];
  if (!spec?.local) return null;
  let cache = toneCache.get(raw);
  if (!cache) { cache = new Map(); toneCache.set(raw, cache); }
  let hit = cache.get(styleName);
  if (!hit) {
    const stretches = spec.channels.map((name) =>
      cachedPercentileStretch(raw, name, spec.lo, spec.hi, 8));
    const { lum, valid } = sceneLuminance(raw, spec, stretches, {});
    hit = buildToneMap(lum, valid, raw.width, raw.height, spec.local);
    cache.set(styleName, hit);
  }
  return hit;
}

function compositeLocalTone(raw: RawScene, spec: SceneStyle, stretches: Stretch[],
                            transforms: BandFits, tone: ToneMap): ImageData {
  const local = spec.local!;
  const { width, height, bands } = raw;
  const { lum, valid } = sceneLuminance(raw, spec, stretches, transforms);
  const out = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (!valid[i]) continue;                 // leave alpha 0
      const o = i * 4;
      const l = lum[i]!;
      const mapped = l + (toneMapValue(tone, x, y, l) - l) * local.amount;
      // Scaling every channel by mapped/l is an exact hue-preserving transform
      // (it moves the pixel along its own colour ray); the cap stops a
      // near-black pixel from being multiplied into a coloured spark.
      const gain = Math.min(8, mapped / Math.max(l, 1 / 512)) * local.sat;
      for (let c = 0; c < 3; c++) {
        const { lo, hi } = stretches[c]!;
        const value = stretch(bandValue(bands, spec.channels[c]!, i, transforms),
                              lo, hi, spec.gamma);
        out[o + c] = Math.round(255 * (mapped + (value - l) * gain));
      }
      out[o + 3] = 255;
    }
  }
  return new ImageData(out, width, height);
}

export function compositeScene(raw: RawScene, styleName: string,
                               options: { stretches?: Stretch[]; transforms?: BandFits | null;
                                          tone?: ToneMap | null } = {}): ImageData {
  if (INDEX_STYLES[styleName]) {
    return compositeIndexStyle(raw, styleName, options.transforms ?? {});
  }
  const spec = SCENE_STYLES[styleName] ?? SCENE_STYLES.truecolor!;
  const { width, height, bands } = raw;
  const mask = bands.dataMask!;
  const stretches = options.stretches
    ?? spec.channels.map((name) => cachedPercentileStretch(raw, name, spec.lo, spec.hi, 8));
  const transforms = options.transforms ?? {};
  if (spec.local) {
    const tone = options.tone ?? sceneToneMap(raw, styleName)!;
    return compositeLocalTone(raw, spec, stretches, transforms, tone);
  }
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (mask[i]! <= SCALE / 2) continue; // leave alpha 0
    for (let c = 0; c < 3; c++) {
      const { lo, hi } = stretches[c]!;
      const bandName = spec.channels[c]!;
      const fit = transforms[bandName];
      const value = fit ? bands[bandName]![i]! * fit.gain + fit.offset : bands[bandName]![i]!;
      out[o + c] = Math.round(stretch(value, lo, hi, spec.gamma) * 255);
    }
    out[o + 3] = 255;
  }
  return new ImageData(out, width, height);
}

type RGB = [number, number, number];

function lerpColor(a: number[], b: number[], t: number): number[] {
  const q = Math.min(1, Math.max(0, t));
  return a.map((v, i) => Math.round(v + (b[i]! - v) * q));
}

function colorStops(value: number, stops: [number, RGB][]): number[] {
  if (value <= stops[0]![0]) return stops[0]![1];
  for (let i = 1; i < stops.length; i++) {
    if (value <= stops[i]![0]) {
      const [v0, c0] = stops[i - 1]!;
      const [v1, c1] = stops[i]!;
      return lerpColor(c0, c1, (value - v0) / Math.max(1e-6, v1 - v0));
    }
  }
  return stops[stops.length - 1]![1];
}

export const INDEX_STYLES: Record<string, { a: string; b: string; stops: [number, RGB][] }> = {
  ndvi: {
    a: 'B08', b: 'B04',
    stops: [[-0.2, [55, 78, 105]], [0, [93, 111, 121]], [0.2, [174, 139, 90]],
            [0.5, [48, 155, 68]], [0.85, [15, 82, 43]]],
  },
  nbr: {
    a: 'B08', b: 'B12',
    stops: [[-0.45, [74, 24, 95]], [-0.1, [142, 74, 88]], [0.1, [186, 151, 102]],
            [0.4, [88, 145, 76]], [0.85, [19, 88, 55]]],
  },
  ndmi: {
    a: 'B08', b: 'B11',
    stops: [[-0.5, [139, 70, 37]], [-0.1, [210, 147, 78]], [0.1, [222, 215, 171]],
            [0.4, [70, 151, 165]], [0.8, [24, 73, 122]]],
  },
  // Water indices, for telling flooding / wet ground apart from damage: rubble
  // is bright in SWIR where water is near-black, so both indices put water at
  // the blue end while debris stays in the browns. NDWI (green vs NIR) is the
  // open-water classic; MNDWI (green vs SWIR1) also lights up wet soil, damp
  // asphalt and turbid shallow water — the exact surfaces that mimic damage in
  // a visible-band comparison.
  ndwi: {
    a: 'B03', b: 'B08',
    stops: [[-0.5, [125, 91, 55]], [-0.15, [190, 168, 124]], [0.05, [216, 226, 229]],
            [0.3, [96, 166, 214]], [0.7, [16, 71, 148]]],
  },
  mndwi: {
    a: 'B03', b: 'B11',
    stops: [[-0.6, [140, 98, 57]], [-0.2, [196, 172, 128]], [0, [220, 224, 222]],
            [0.25, [110, 178, 220]], [0.7, [14, 64, 140]]],
  },
};

// Single-date diagnostic indices are often more interpretable than a damage
// mask. Fixed color scales keep the same value the same color on both dates.
function compositeIndexStyle(raw: RawScene, styleName: string,
                             transforms: BandFits = {}): ImageData {
  const spec = INDEX_STYLES[styleName] ?? INDEX_STYLES.ndvi!;
  const { width, height, bands } = raw;
  const mask = bands.dataMask!;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (mask[i]! <= SCALE / 2) continue;
    const fa = transforms[spec.a];
    const fb = transforms[spec.b];
    const a = fa ? bands[spec.a]![i]! * fa.gain + fa.offset : bands[spec.a]![i]!;
    const b0 = fb ? bands[spec.b]![i]! * fb.gain + fb.offset : bands[spec.b]![i]!;
    const value = (a - b0) / (a + b0 + 1e-6);
    const [r, g, b] = colorStops(value, spec.stops);
    out[o] = r!; out[o + 1] = g!; out[o + 2] = b!; out[o + 3] = 255;
  }
  return new ImageData(out, width, height);
}

// Render both dates against the reference scene's tonal scale. The latest
// scene is first fitted per band to stable reference pixels, then uses the same
// stretch/color scale, so a wipe does not pulse merely because sun or haze did.
export function compositeScenePair(rawBefore: RawScene, rawAfter: RawScene, styleName: string,
                                   { normalize = true, normStrength = 1 } = {}):
    { before: ImageData; after: ImageData; fits: BandFits | null } {
  if (!normalize) {
    return { before: compositeScene(rawBefore, styleName),
             after: compositeScene(rawAfter, styleName), fits: null };
  }
  const fits = scaleFits(radiometricFit(rawBefore, rawAfter,
                                        isSarStyle(styleName) ? SAR_BANDS : RAW_BANDS),
                         normStrength);
  if (INDEX_STYLES[styleName]) {
    return {
      before: compositeIndexStyle(rawBefore, styleName),
      after: compositeIndexStyle(rawAfter, styleName, fits),
      fits,
    };
  }
  const spec = SCENE_STYLES[styleName] ?? SCENE_STYLES.truecolor!;
  const stretches = spec.channels.map((name) =>
    cachedPercentileStretch(rawBefore, name, spec.lo, spec.hi, 8));
  // Both dates share the reference scene's tone curves for the same reason they
  // share its stretch: a locally-equalized after scene would re-expose every
  // tile to its own histogram, so a wipe would pulse wherever the ground merely
  // got brighter — exactly the change the display must not invent.
  const tone = sceneToneMap(rawBefore, styleName);
  return {
    before: compositeScene(rawBefore, styleName, { stretches, tone }),
    after: compositeScene(rawAfter, styleName, { stretches, tone, transforms: fits }),
    fits,
  };
}

export const CORE_BANDS = ['B02', 'B03', 'B04', 'B08', 'B11', 'B12'];
export const EXTENDED_BANDS = RAW_BANDS.slice();

// Map a 1-10 "sensitivity" dial to the RMS normalization divisor.
function sensitivityToDivisor(sensitivity: number): number {
  const s = Math.min(10, Math.max(1, sensitivity));
  return 0.30 - ((s - 1) / 9) * (0.30 - 0.06); // s=1 -> 0.30 (calm), s=10 -> 0.06 (hot)
}

function ramp(t: number): [number, number, number, number] {
  if (t < 0.15) return [0, 0, 0, 0];
  const s = (t - 0.15) / 0.85;
  return [255, Math.max(0, 255 * (1 - s * 1.2)), 0, Math.min(255, (0.35 + s * 0.65) * 255)];
}

// Per-band linear alignment (gain + offset) fitting the AFTER scene onto the
// BEFORE scene: before ≈ gain·after + offset, by least squares over pixels
// valid in both dates. Two acquisitions of the same ground differ mostly by a
// predictable, scene-wide illumination/haze shift (sun angle, atmosphere), which
// a straight line captures far better than a single gain — cancelling it leaves
// the residual (genuine ground change) to drive the difference view. Real change
// is a small fraction of pixels, so it barely perturbs the fit; the clamps stop
// a pathological scene from being "corrected" flat. Offset is in raw DN units.
export function radiometricFit(rawBefore: RawScene, rawAfter: RawScene,
                               bandNames: string[]): BandFits {
  let byAfter = fitCache.get(rawBefore);
  if (!byAfter) {
    byAfter = new WeakMap();
    fitCache.set(rawBefore, byAfter);
  }
  let byBands = byAfter.get(rawAfter);
  if (!byBands) {
    byBands = new Map();
    byAfter.set(rawAfter, byBands);
  }
  const cacheKey = [...bandNames].sort().join('|');
  const cached = byBands.get(cacheKey);
  if (cached) return cached;

  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const fits: BandFits = {};
  for (const name of bandNames) {
    const bBand = rawBefore.bands[name]!;
    const aBand = rawAfter.bands[name]!;
    const pairs: [number, number][] = [];
    for (let i = 0; i < bBand.length; i += 7) {
      if (maskB[i]! > SCALE / 2 && maskA[i]! > SCALE / 2) pairs.push([aBand[i]!, bBand[i]!]);
    }
    if (pairs.length < 50) { fits[name] = { gain: 1, offset: 0 }; continue; }

    // Quantile initialization is insensitive to changed outliers and gives the
    // correct broad scale/offset before the stable-pixel regression below.
    const xs = pairs.map((p) => p[0]).sort((a, b) => a - b);
    const ys = pairs.map((p) => p[1]).sort((a, b) => a - b);
    const q = (arr: number[], p: number) => arr[Math.floor(p * (arr.length - 1))]!;
    const x25 = q(xs, .25), x50 = q(xs, .5), x75 = q(xs, .75);
    const y25 = q(ys, .25), y50 = q(ys, .5), y75 = q(ys, .75);
    let gain = (x75 - x25) > 1 ? (y75 - y25) / (x75 - x25) : 1;
    gain = Math.min(1.6, Math.max(0.6, gain));
    let offset = y50 - gain * x50;

    // Two IRLS-like passes retain pseudo-invariant pixels. Median absolute
    // residual rejects real land-cover change, cloud edges, and hot pixels.
    for (let pass = 0; pass < 2; pass++) {
      const residuals = pairs.map(([x, y]) => Math.abs(y - (gain * x + offset)))
        .sort((a, b) => a - b);
      const mad = residuals[residuals.length >> 1] || 1;
      const limit = Math.max(80, 3.5 * mad);
      let n = 0, sx = 0, sy = 0, sxx = 0, sxy = 0;
      for (const [x, y] of pairs) {
        if (Math.abs(y - (gain * x + offset)) > limit) continue;
        n++; sx += x; sy += y; sxx += x * x; sxy += x * y;
      }
      if (n < 40) break;
      const denom = n * sxx - sx * sx;
      if (Math.abs(denom) < 1e-9) break;
      gain = Math.min(1.6, Math.max(0.6, (n * sxy - sx * sy) / denom));
      offset = (sy - gain * sx) / n;
    }
    // Keep the additive term within ~half-reflectance so a bad fit can't invent
    // change; it only removes gentle haze/illumination pedestals.
    offset = Math.min(0.15 * SCALE, Math.max(-0.15 * SCALE, offset));
    fits[name] = { gain, offset };
  }
  byBands.set(cacheKey, fits);
  return fits;
}

// Interpolate a radiometric fit toward identity. The full fit models the whole
// scene-wide illumination shift — which is also its failure mode: when a large
// fraction of the frame genuinely changed (a broad burn, a flood), the
// stable-pixel regression partially absorbs that change into the "correction"
// and the difference view goes quiet exactly where it matters. Strength < 1
// keeps only part of the correction: enough to calm sun and haze, not enough
// to swallow a scene-wide event.
export function scaleFits(fits: BandFits, strength: number): BandFits {
  const s = Math.min(1, Math.max(0, strength));
  if (s === 1) return fits;
  const out: BandFits = {};
  for (const [name, fit] of Object.entries(fits)) {
    out[name] = { gain: 1 + (fit.gain - 1) * s, offset: fit.offset * s };
  }
  return out;
}

function bilinearSample(band: ArrayLike<number>, width: number, height: number,
                        x: number, y: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y), x1 = x0 + 1, y1 = y0 + 1;
  if (x0 < 0 || y0 < 0 || x1 >= width || y1 >= height) return NaN;
  const tx = x - x0, ty = y - y0;
  const p00 = band[y0 * width + x0]!, p10 = band[y0 * width + x1]!;
  const p01 = band[y1 * width + x0]!, p11 = band[y1 * width + x1]!;
  return (p00 * (1 - tx) + p10 * tx) * (1 - ty) + (p01 * (1 - tx) + p11 * tx) * ty;
}

function registrationError(rawBefore: RawScene, rawAfter: RawScene,
                           dx: number, dy: number, fits: BandFits,
                           names: string[], step = 4): number {
  const { width, height } = rawBefore;
  const mb = rawBefore.bands.dataMask!;
  const ma = rawAfter.bands.dataMask!;
  let sum = 0, weightSum = 0;
  for (let y = 3; y < height - 3; y += step) {
    for (let x = 3; x < width - 3; x += step) {
      const i = y * width + x;
      const sx = x + dx, sy = y + dy;
      const ni = Math.round(sy) * width + Math.round(sx);
      if (mb[i]! <= SCALE / 2 || ni < 0 || ni >= ma.length || ma[ni]! <= SCALE / 2) continue;
      for (const name of names) {
        const before = rawBefore.bands[name]!;
        const sampled = bilinearSample(rawAfter.bands[name]!, width, height, sx, sy);
        if (!Number.isFinite(sampled)) continue;
        const fit = fits[name]!;
        const residual = Math.abs((sampled * fit.gain + fit.offset) - before[i]!) / SCALE;
        // Edge-rich pixels constrain the registration, while the capped loss
        // keeps real damage/cloud edges from pulling the whole scene toward it.
        const texture = Math.abs(before[i + 1]! - before[i - 1]!)
          + Math.abs(before[i + width]! - before[i - width]!);
        const weight = Math.min(1, Math.max(.12, texture / 700));
        sum += Math.min(residual, .08) * weight;
        weightSum += weight;
      }
    }
  }
  return weightSum > 100 ? sum / weightSum : Infinity;
}

export interface SubpixelShift { dx: number; dy: number; improvement: number }

// Robust coarse-to-fine sub-pixel translation. Sentinel processing already
// places both acquisitions on the same projection, so a bounded translation is
// safer than a local elastic warp (which could literally warp real damage away).
export function estimateSubpixelShift(rawBefore: RawScene, rawAfter: RawScene,
                                      maxShift = 4): SubpixelShift {
  // SAR has no B08/B04; align on VV (its most structured band). Optical keeps
  // NIR structure + visible edges.
  const names = rawBefore.bands.VV ? ['VV'] : ['B08', 'B04'];
  const fits = radiometricFit(rawBefore, rawAfter, names);
  const zeroErr = registrationError(rawBefore, rawAfter, 0, 0, fits, names);
  let best = { dx: 0, dy: 0, err: zeroErr };
  for (let dy = -maxShift; dy <= maxShift; dy++) {
    for (let dx = -maxShift; dx <= maxShift; dx++) {
      const err = registrationError(rawBefore, rawAfter, dx, dy, fits, names);
      if (err < best.err) best = { dx, dy, err };
    }
  }
  for (const increment of [.5, .25, .125]) {
    let refined = best;
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        const dx = best.dx + ox * increment, dy = best.dy + oy * increment;
        if (Math.abs(dx) > maxShift || Math.abs(dy) > maxShift) continue;
        const err = registrationError(rawBefore, rawAfter, dx, dy, fits, names, 3);
        if (err < refined.err) refined = { dx, dy, err };
      }
    }
    best = refined;
  }
  if (!Number.isFinite(zeroErr) || !(best.err < zeroErr * .99)) {
    return { dx: 0, dy: 0, improvement: 0 };
  }
  return {
    dx: Math.round(best.dx * 100) / 100,
    dy: Math.round(best.dy * 100) / 100,
    improvement: Math.max(0, (zeroErr - best.err) / zeroErr),
  };
}

// Resample every latest-scene band at its registered sub-pixel coordinate.
// Bilinear interpolation makes fractional shifts meaningful; the output mask
// only accepts pixels whose four source neighbors are all valid.
export function applySubpixelShift(raw: RawScene, dx: number, dy: number): RawScene {
  if (dx === 0 && dy === 0) return raw;
  const { width, height, bands } = raw;
  const names = Object.keys(bands).filter((name) => name !== 'dataMask');
  const Mask = bands.dataMask!.constructor as new (n: number) => Uint16Array;
  const out: Record<string, Uint16Array | Float32Array> = { dataMask: new Mask(width * height) };
  for (const name of names) {
    const Band = bands[name]!.constructor as new (n: number) => Uint16Array;
    out[name] = new Band(width * height);
  }
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x, sx = x + dx, sy = y + dy;
      const x0 = Math.floor(sx), y0 = Math.floor(sy), x1 = x0 + 1, y1 = y0 + 1;
      if (x0 < 0 || y0 < 0 || x1 >= width || y1 >= height) continue;
      const i00 = y0 * width + x0, i10 = i00 + 1;
      const i01 = i00 + width, i11 = i01 + 1;
      if (bands.dataMask![i00]! <= SCALE / 2 || bands.dataMask![i10]! <= SCALE / 2 ||
          bands.dataMask![i01]! <= SCALE / 2 || bands.dataMask![i11]! <= SCALE / 2) continue;
      const tx = sx - x0, ty = sy - y0;
      const w00 = (1 - tx) * (1 - ty), w10 = tx * (1 - ty);
      const w01 = (1 - tx) * ty, w11 = tx * ty;
      out.dataMask![i] = SCALE;
      for (const name of names) {
        const band = bands[name]!;
        out[name]![i] = band[i00]! * w00 + band[i10]! * w10 + band[i01]! * w01 + band[i11]! * w11;
      }
    }
  }
  return { width, height, bands: out };
}

// Overwrite `baseImg` pixels with `overlayImg` pixels wherever `mask` is set,
// returning a new ImageData. Used to build the "frozen background" after-scene:
// the before scene everywhere, replaced by the real after scene only where the
// change mask fired — so unchanged ground is pixel-identical between the dates.
export function mergeByMask(baseImg: ImageData, overlayImg: ImageData,
                            mask: Uint8Array): ImageData {
  const out = new Uint8ClampedArray(baseImg.data);
  const od = overlayImg.data;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      const o = i * 4;
      out[o] = od[o]!; out[o + 1] = od[o + 1]!;
      out[o + 2] = od[o + 2]!; out[o + 3] = od[o + 3]!;
    }
  }
  return new ImageData(out, baseImg.width, baseImg.height);
}

// 3x3 box smoothing over a Float32 score map. Single isolated hot pixels
// (sensor noise, mis-registration at edges) get averaged down toward their
// neighborhood, while real multi-pixel damage patches survive — a cheap
// speckle filter for the change layers.
function boxSmooth3(src: Float32Array, width: number, height: number): Float32Array {
  const horizontal = new Float32Array(src.length);
  const dst = new Float32Array(src.length);
  for (let y = 0; y < height; y++) {
    const row = y * width;
    let sum = 0;
    for (let x = 0; x < width; x++) {
      if (x === 0) sum = src[row]! + (width > 1 ? src[row + 1]! : 0);
      else {
        if (x + 1 < width) sum += src[row + x + 1]!;
        if (x - 2 >= 0) sum -= src[row + x - 2]!;
      }
      horizontal[row + x] = sum / (Math.min(width - 1, x + 1) - Math.max(0, x - 1) + 1);
    }
  }
  for (let x = 0; x < width; x++) {
    let sum = 0;
    for (let y = 0; y < height; y++) {
      if (y === 0) sum = horizontal[x]! + (height > 1 ? horizontal[width + x]! : 0);
      else {
        if (y + 1 < height) sum += horizontal[(y + 1) * width + x]!;
        if (y - 2 >= 0) sum -= horizontal[(y - 2) * width + x]!;
      }
      dst[y * width + x] = sum / (Math.min(height - 1, y + 1) - Math.max(0, y - 1) + 1);
    }
  }
  return dst;
}

function removeSmallComponents(mask: Uint8Array, width: number, height: number,
                               minPixels = 3): Uint8Array {
  const visited = new Uint8Array(mask.length);
  const clean = new Uint8Array(mask.length);
  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || visited[start]) continue;
    const stack = [start];
    const component: number[] = [];
    visited[start] = 1;
    while (stack.length) {
      const i = stack.pop()!;
      component.push(i);
      const x = i % width, y = Math.floor(i / width);
      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          if (ox === 0 && oy === 0) continue;
          const xx = x + ox, yy = y + oy;
          if (xx < 0 || yy < 0 || xx >= width || yy >= height) continue;
          const j = yy * width + xx;
          if (mask[j] && !visited[j]) { visited[j] = 1; stack.push(j); }
        }
      }
    }
    if (component.length >= minPixels) for (const i of component) clean[i] = 1;
  }
  return clean;
}

export function removeSmallDetections(imageData: ImageData, minPixels = 3): ImageData {
  const { width, height, data } = imageData;
  const mask = new Uint8Array(width * height);
  for (let i = 0; i < mask.length; i++) if (data[i * 4 + 3]! > 0) mask[i] = 1;
  const clean = removeSmallComponents(mask, width, height, minPixels);
  const out = new Uint8ClampedArray(data);
  for (let i = 0; i < clean.length; i++) if (!clean[i]) out[i * 4 + 3] = 0;
  return new ImageData(out, width, height);
}

export interface ChangeOptions {
  bandSet: 'core' | 'extended';
  sensitivity: number;
  normalize?: boolean;
  /** 0–1 fraction of the radiometric fit to apply (see scaleFits). */
  normStrength?: number;
  despeckle?: boolean;
  /** Drop change explained by the ground getting WETTER (flooding, pools,
   *  rain-damp roads) — the surfaces that mimic damage in a difference view. */
  ignoreWater?: boolean;
  /** Drop change where vegetation/burn indices ROSE — regrowth and burn-scar
   *  recovery, which is change but never damage. */
  ignoreRecovery?: boolean;
}

export interface ChangeResult {
  imageData: ImageData;
  mask: Uint8Array;
  /** Post-smoothing score map in 0–1 (same scale the ramp thresholds at 0.15),
   *  so freeze-unchanged can pick its own, lower cutoff. */
  score: Float32Array;
  valid: Uint8Array;
}

const EPS = 1e-6;

// True when the change at pixel i is EXPLAINED AWAY by an enabled gate. The
// before scene is read raw; the after scene through the same fits the score
// used, so the gate judges exactly the pair the score saw.
//
// Water: wetting DARKENS the ground while MNDWI (green vs SWIR1) swings
// positive — SWIR collapses first when a surface wets. Rubble and blast scars
// brighten instead, so the darkening condition is what keeps fresh damage out
// of the gate even when dust shifts the band ratios. The NDWI arm needs the
// after pixel to actually read as water (ndwiA > 0.05): a burn also raises
// NDWI (NIR collapses), and without the absolute check this gate would silently
// swallow burn damage — the one thing it must never do.
//
// Recovery: NBR rising means NIR back up relative to SWIR2 — regrowth, or a
// burn scar fading. New water also lifts NBR, which is fine: both directions
// mean "not fresh damage" to the reader who enabled these.
function gateSuppresses(bandsB: RawScene['bands'], bandsA: RawScene['bands'],
                        fits: BandFits, i: number,
                        ignoreWater: boolean, ignoreRecovery: boolean): boolean {
  const gB = bandsB.B03![i]! / SCALE, nirB = bandsB.B08![i]! / SCALE;
  const s1B = bandsB.B11![i]! / SCALE, s2B = bandsB.B12![i]! / SCALE;
  const after = (name: string): number => {
    const fit = fits[name];
    const raw = bandsA[name]![i]!;
    return Math.max(0, fit ? raw * fit.gain + fit.offset : raw) / SCALE;
  };
  const gA = after('B03'), nirA = after('B08');
  const s1A = after('B11'), s2A = after('B12');
  if (ignoreWater) {
    const dMndwi = (gA - s1A) / (gA + s1A + EPS) - (gB - s1B) / (gB + s1B + EPS);
    const ndwiA = (gA - nirA) / (gA + nirA + EPS);
    const dNdwi = ndwiA - (gB - nirB) / (gB + nirB + EPS);
    const darkened = gA + nirA + s1A < gB + nirB + s1B + 0.01;
    if (darkened && (dMndwi > 0.12 || (ndwiA > 0.05 && dNdwi > 0.12))) return true;
  }
  if (ignoreRecovery) {
    const dNbr = (nirA - s2A) / (nirA + s2A + EPS) - (nirB - s2B) / (nirB + s2B + EPS);
    if (dNbr > 0.05) return true;
  }
  return false;
}

function canGate(rawBefore: RawScene, rawAfter: RawScene,
                 ignoreWater?: boolean, ignoreRecovery?: boolean): boolean {
  return !!(ignoreWater || ignoreRecovery)
    && !!(rawBefore.bands.B03 && rawBefore.bands.B08 && rawBefore.bands.B11
          && rawBefore.bands.B12 && rawAfter.bands.B03);
}

function dilate1(mask: Uint8Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(mask.length);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (!mask[i]) continue;
      out[i] = 1;
      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          const xx = x + ox, yy = y + oy;
          if (xx >= 0 && yy >= 0 && xx < width && yy < height) out[yy * width + xx] = 1;
        }
      }
    }
  }
  return out;
}

export interface FreezeOptions {
  /** 0–1: how far BELOW the visible change threshold the after image still
   *  shows through. 0 freezes everything the ramp leaves transparent — the old
   *  behaviour, whose failure mode was erasing real-but-faint change from the
   *  B view entirely. */
  keepFaint?: number;
  dilatePx?: number;
}

// The keep-mask for freeze-unchanged: 1 = show the real AFTER pixel. Frozen
// pixels come from the BEFORE scene, so anything this mask misses is literally
// removed from the display — which is why it cuts below the visible threshold
// (keepFaint) and dilates one pixel: the rim of a change patch is exactly where
// the score straddles the cutoff, and freezing the rim amputates the shape.
export function freezeKeepMask(score: Float32Array, width: number, height: number,
                               { keepFaint = 0.4, dilatePx = 1 }: FreezeOptions = {}): Uint8Array {
  const cutoff = 0.15 * (1 - Math.min(0.9, Math.max(0, keepFaint)));
  let keep = new Uint8Array(score.length);
  for (let i = 0; i < score.length; i++) if (score[i]! >= cutoff) keep[i] = 1;
  for (let d = 0; d < dilatePx; d++) keep = dilate1(keep, width, height);
  return keep;
}

// Difference-isolation change layer (CVA). First aligns the two dates per band
// with a gain+offset fit (radiometricFit) so the whole scene's predictable
// illumination shift is cancelled; what remains is the RMS spectral residual —
// genuine ground change. Pixels below the sensitivity threshold are left FULLY
// TRANSPARENT, so when this layer sits over the base scene the unchanged
// ground shows through pixel-for-pixel identical.
export function computeChange(rawBefore: RawScene, rawAfter: RawScene,
                              { bandSet, sensitivity, normalize = true, normStrength = 1,
                                despeckle = false, ignoreWater = false, ignoreRecovery = false }:
                              ChangeOptions): ChangeResult {
  const bandsList = bandSet === 'core' ? CORE_BANDS : EXTENDED_BANDS;
  const divisor = sensitivityToDivisor(sensitivity);
  const { width, height } = rawBefore;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const n = bandsList.length;
  const bBands = bandsList.map((name) => rawBefore.bands[name]!);
  const aBands = bandsList.map((name) => rawAfter.bands[name]!);
  const fits = normalize
    ? scaleFits(radiometricFit(rawBefore, rawAfter, bandsList), normStrength) : null;
  const gainArr = bandsList.map((name) => (fits ? fits[name]!.gain : 1));
  const offArr = bandsList.map((name) => (fits ? fits[name]!.offset : 0));
  const gating = canGate(rawBefore, rawAfter, ignoreWater, ignoreRecovery);
  const gateFits = fits ?? {};

  let score = new Float32Array(width * height);
  const valid = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    valid[i] = 1;
    let sumSq = 0;
    for (let k = 0; k < n; k++) {
      // Align AFTER onto BEFORE, then measure the leftover per-band residual.
      const aligned = aBands[k]![i]! * gainArr[k]! + offArr[k]!;
      const d = (aligned - bBands[k]![i]!) / SCALE;
      sumSq += d * d;
    }
    let s = Math.min(Math.sqrt(sumSq / n) / divisor, 1.0);
    if (s > 0.02 && gating
        && gateSuppresses(rawBefore.bands, rawAfter.bands, gateFits, i,
                          ignoreWater, ignoreRecovery)) {
      s = 0;
    }
    score[i] = s;
  }
  if (despeckle) score = boxSmooth3(score, width, height);

  const out = new Uint8ClampedArray(width * height * 4);
  let mask = new Uint8Array(width * height); // 1 where change is above the visible threshold
  for (let i = 0; i < width * height; i++) {
    if (!valid[i]) continue;
    const o = i * 4;
    const [r, g, b, a] = ramp(score[i]!);
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
    if (score[i]! >= 0.15) mask[i] = 1; // matches ramp()'s transparency cutoff
  }
  if (despeckle) {
    mask = removeSmallComponents(mask, width, height, 3);
    for (let i = 0; i < mask.length; i++) if (!mask[i]) out[i * 4 + 3] = 0;
  }
  return { imageData: new ImageData(out, width, height), mask, score, valid };
}

// SAR amplitude change (log-ratio). SAR has no spectral bands — the change
// signal is how much the backscatter ROSE or FELL between passes, which the
// log-ratio ln(after/before) captures symmetrically and (being a ratio) largely
// cancels the multiplicative speckle a plain difference would amplify. VV and VH
// are combined by magnitude; the sign of the stronger VV drives a diverging
// colour so you can read the direction: brighter (new hard structures, rougher
// surface) vs darker (flooding, smoothing, cleared ground).
//
// The threshold is in units of THIS PAIR'S OWN NOISE, never a fixed dB. Measured
// 2026-09-27 on two ascending-28 passes 12 days apart over Isfahan at the
// Studio's ~190 m: pass-to-pass scatter is σ ≈ 0.6 dB per polarisation (a Lee
// filter server-side doesn't move it — the render already averages), and the
// old fixed 0.6 dB cut painted 54% of the ground, so a VV or VH view read as
// tinted instead of grey. σ comes from the pair (median-centred MAD, so any
// residual global offset cancels too); a pixel counts when its combined
// deviation passes sarZThreshold(sensitivity)·σ AND SAR_MIN_DB.
export const SAR_MIN_DB = 1;
const SAR_SIGMA_FLOOR_DB = 0.2;   // two near-identical passes must not make 0.1 dB "change"

/** Noise multiple a SAR pixel must exceed: 5σ calm (s=1) → 2.5σ hot (s=10).
 *  For pure noise the combined z is chi-distributed with 2 dof, so the false
 *  alarm rate is exp(-z²/2): ~0.05% at the default, ~4% at the hottest. */
export function sarZThreshold(sensitivity: number): number {
  const s = Math.min(10, Math.max(1, sensitivity));
  return 5 - ((s - 1) / 9) * 2.5;
}

/** Median and MAD-sigma of `field` over valid pixels (sampled to ≤200k). */
export function robustSpread(field: Float32Array, valid: Uint8Array):
    { median: number; sigma: number } {
  let count = 0;
  for (let i = 0; i < valid.length; i++) if (valid[i]) count++;
  if (!count) return { median: 0, sigma: SAR_SIGMA_FLOOR_DB };
  const stride = Math.max(1, Math.floor(count / 200_000));
  const sample = new Float32Array(Math.ceil(count / stride));
  let k = 0, m = 0;
  for (let i = 0; i < valid.length; i++) {
    if (!valid[i]) continue;
    if (k++ % stride === 0) sample[m++] = field[i]!;
  }
  const values = sample.subarray(0, m).sort();
  const median = values[m >> 1]!;
  const dev = values.map((v) => Math.abs(v - median)).sort();
  return { median, sigma: Math.max(SAR_SIGMA_FLOOR_DB, 1.4826 * dev[m >> 1]!) };
}

export function computeSarChange(rawBefore: RawScene, rawAfter: RawScene,
                                 { sensitivity, normalize = true, normStrength = 1,
                                   despeckle = false }: ChangeOptions): ChangeResult {
  const { width, height } = rawBefore;
  const n = width * height;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const vvB = rawBefore.bands.VV!, vhB = rawBefore.bands.VH!;
  const vvA = rawAfter.bands.VV!, vhA = rawAfter.bands.VH!;
  const fits = normalize
    ? scaleFits(radiometricFit(rawBefore, rawAfter, SAR_BANDS), normStrength) : null;
  const gVV = fits ? fits.VV!.gain : 1, oVV = fits ? fits.VV!.offset : 0;
  const gVH = fits ? fits.VH!.gain : 1, oVH = fits ? fits.VH!.offset : 0;
  const eps = 8; // DN floor so near-zero backscatter can't blow the ratio up

  let dVV = new Float32Array(n);   // signed log-ratios, in dB
  let dVH = new Float32Array(n);
  const valid = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    valid[i] = 1;
    dVV[i] = 4.343 * Math.log((Math.max(0, vvA[i]! * gVV + oVV) + eps) / (vvB[i]! + eps));
    dVH[i] = 4.343 * Math.log((Math.max(0, vhA[i]! * gVH + oVH) + eps) / (vhB[i]! + eps));
  }
  // Smooth BEFORE measuring the noise, so σ describes the field being judged.
  if (despeckle) { dVV = boxSmooth3(dVV, width, height); dVH = boxSmooth3(dVH, width, height); }
  const nv = robustSpread(dVV, valid), nh = robustSpread(dVH, valid);
  const zThr = sarZThreshold(sensitivity);
  const zFull = zThr * 2.5;

  const out = new Uint8ClampedArray(n * 4);
  let mask = new Uint8Array(n);
  const score = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    if (!valid[i]) continue;
    const vv = dVV[i]! - nv.median, vh = dVH[i]! - nh.median;
    const z = Math.hypot(vv / nv.sigma, vh / nh.sigma);
    const db = Math.hypot(vv, vh);
    // 0.15 at the threshold and 1 at zFull: the scale every layer's score
    // uses, so Freeze unchanged and Keep faint read it unchanged.
    let t = z < zThr ? 0.15 * z / zThr
      : 0.15 + 0.85 * Math.min(1, (z - zThr) / (zFull - zThr));
    if (db < SAR_MIN_DB) t = Math.min(t, 0.15 * db / SAR_MIN_DB);
    score[i] = t;
    if (t < 0.15) continue; // below threshold -> transparent
    const o = i * 4;
    const alpha = Math.round((0.38 + 0.62 * Math.min(1, (t - 0.15) / 0.85)) * 255);
    const c = vv >= 0
      ? lerpColor([255, 214, 120], [209, 40, 40], t)   // brighter: warm -> red
      : lerpColor([150, 210, 240], [30, 90, 200], t);  // darker: pale -> blue
    out[o] = c[0]!; out[o + 1] = c[1]!; out[o + 2] = c[2]!; out[o + 3] = alpha;
    mask[i] = 1;
  }
  if (despeckle) {
    mask = removeSmallComponents(mask, width, height, 3);
    for (let i = 0; i < mask.length; i++) if (!mask[i]) out[i * 4 + 3] = 0;
  }
  return { imageData: new ImageData(out, width, height), mask, score, valid };
}

function spectralRamp(t: number): [number, number, number, number] {
  if (t < .15) return [0, 0, 0, 0];
  const s = Math.min(1, (t - .15) / .85);
  const color = s < .5
    ? lerpColor([98, 182, 203], [242, 193, 78], s * 2)
    : lerpColor([242, 193, 78], [209, 73, 91], (s - .5) * 2);
  return [color[0]!, color[1]!, color[2]!, Math.round((.38 + .62 * s) * 255)];
}

// Spectral Angle Mapper change: compares the direction of each multispectral
// vector instead of only its absolute brightness. A uniformly darker/brighter
// acquisition stays similar, while a real material/land-cover change rotates
// the vector and becomes visible.
export function computeSpectralAngleChange(rawBefore: RawScene, rawAfter: RawScene,
                                           { bandSet, sensitivity, normalize = true,
                                             normStrength = 1, despeckle = false,
                                             ignoreWater = false, ignoreRecovery = false,
                                           }: ChangeOptions): ChangeResult {
  const bandsList = bandSet === 'core' ? CORE_BANDS : EXTENDED_BANDS;
  const { width, height } = rawBefore;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const fits = normalize
    ? scaleFits(radiometricFit(rawBefore, rawAfter, bandsList), normStrength) : null;
  const gating = canGate(rawBefore, rawAfter, ignoreWater, ignoreRecovery);
  const gateFits = fits ?? {};
  const divisor = sensitivityToDivisor(sensitivity) * 1.15;
  let score = new Float32Array(width * height);
  const valid = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    let dot = 0, normB = 0, normA = 0;
    for (const name of bandsList) {
      const b = Math.max(0, rawBefore.bands[name]![i]!) / SCALE;
      const fit = fits && fits[name];
      const a = Math.max(0, fit ? rawAfter.bands[name]![i]! * fit.gain + fit.offset
                                : rawAfter.bands[name]![i]!) / SCALE;
      dot += a * b; normA += a * a; normB += b * b;
    }
    if (normA < 1e-8 || normB < 1e-8) continue;
    valid[i] = 1;
    const cosine = Math.min(1, Math.max(-1, dot / Math.sqrt(normA * normB)));
    let s = Math.min(Math.acos(cosine) / divisor, 1);
    if (s > 0.02 && gating
        && gateSuppresses(rawBefore.bands, rawAfter.bands, gateFits, i,
                          ignoreWater, ignoreRecovery)) {
      s = 0;
    }
    score[i] = s;
  }
  if (despeckle) score = boxSmooth3(score, width, height);
  const out = new Uint8ClampedArray(width * height * 4);
  let mask = new Uint8Array(width * height);
  for (let i = 0; i < score.length; i++) {
    if (!valid[i]) continue;
    const [r, g, b, a] = spectralRamp(score[i]!);
    const o = i * 4;
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
    if (score[i]! >= .15) mask[i] = 1;
  }
  if (despeckle) {
    mask = removeSmallComponents(mask, width, height, 3);
    for (let i = 0; i < mask.length; i++) if (!mask[i]) out[i * 4 + 3] = 0;
  }
  return { imageData: new ImageData(out, width, height), mask, score, valid };
}

function nbrAt(bands: RawScene['bands'], i: number): number {
  const nir = bands.B08![i]! / SCALE;
  const swir = bands.B12![i]! / SCALE;
  return (nir - swir) / (nir + swir + 1e-6);
}

function dnbrColor(d: number): [number, number, number, number] {
  if (d < 0.1) return [0, 0, 0, 0];
  if (d < 0.27) return [255, 255, 77, 140];   // low severity
  if (d < 0.44) return [255, 166, 26, 179];   // moderate-low
  if (d < 0.66) return [255, 77, 0, 209];     // moderate-high
  return [153, 0, 0, 235];                    // high severity
}

// The burn layers are directional by construction (they colour only where the
// index moved burn-ward), but they still fire on ground that was ALREADY char
// getting slightly more charred. freshOnly requires the before pixel not to
// read as burned — NBR below -0.05 is char/ash territory that even dry bare
// soil doesn't reach — so what remains is the transition the layer exists for:
// NOT burned -> burned. A pixel burned in both dates is a continuing condition,
// not an event.
export interface BurnOptions { freshOnly?: boolean }

const ALREADY_BURNED_NBR = -0.05;

// Burn-severity dNBR: the standard index for fire / blast-fire damage.
export function compositeDNBR(rawBefore: RawScene, rawAfter: RawScene,
                              { freshOnly = false }: BurnOptions = {}): ImageData {
  const { width, height } = rawBefore;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    const nbrBefore = nbrAt(rawBefore.bands, i);
    if (freshOnly && nbrBefore < ALREADY_BURNED_NBR) continue;
    const d = nbrBefore - nbrAt(rawAfter.bands, i);
    const [r, g, b, a] = dnbrColor(d);
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
  }
  return new ImageData(out, width, height);
}

// Ash & char via dNBR2. NBR2 = (B11-B12)/(B11+B12) uses the two SWIR bands
// only — sensitive to surface moisture and char/ash deposits; unlike NBR it
// doesn't depend on NIR vegetation signal, so it flags burned residue even
// where there was little green vegetation to lose (urban fires, dry scrub).
function nbr2At(bands: RawScene['bands'], i: number): number {
  const s1 = bands.B11![i]! / SCALE;
  const s2 = bands.B12![i]! / SCALE;
  return (s1 - s2) / (s1 + s2 + 1e-6);
}

function dnbr2Color(d: number): [number, number, number, number] {
  if (d < 0.05) return [0, 0, 0, 0];
  if (d < 0.12) return [255, 255, 77, 140];   // light ash / drying
  if (d < 0.20) return [255, 166, 26, 179];   // moderate char
  if (d < 0.30) return [255, 77, 0, 209];     // heavy char
  return [153, 0, 0, 235];                    // severe residue
}

export function compositeDNBR2(rawBefore: RawScene, rawAfter: RawScene,
                               { freshOnly = false }: BurnOptions = {}): ImageData {
  const { width, height } = rawBefore;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    // NBR is the "already charred" test even here: NBR2's own before-value
    // reads moisture, not char, so it cannot answer "was this burned already".
    if (freshOnly && nbrAt(rawBefore.bands, i) < ALREADY_BURNED_NBR) continue;
    const d = nbr2At(rawBefore.bands, i) - nbr2At(rawAfter.bands, i);
    const [r, g, b, a] = dnbr2Color(d);
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
  }
  return new ImageData(out, width, height);
}

// Burned-area signal via BAIS2 (Filipponi 2018), built for Sentinel-2: red-edge
// bands catch vegetation structure loss while the SWIR2 term catches char.
// Positive change (after minus before) marks freshly burned surface.
function bais2At(bands: RawScene['bands'], i: number): number {
  const b04 = bands.B04![i]! / SCALE;
  const b06 = bands.B06![i]! / SCALE;
  const b07 = bands.B07![i]! / SCALE;
  const b8a = bands.B8A![i]! / SCALE;
  const b12 = bands.B12![i]! / SCALE;
  const veg = Math.sqrt(Math.max(0, (b06 * b07 * b8a) / (b04 + 1e-4)));
  return (1 - veg) * ((b12 - b8a) / (Math.sqrt(b12 + b8a) + 1e-4) + 1);
}

export function compositeBAIS2(rawBefore: RawScene, rawAfter: RawScene,
                               { freshOnly = false }: BurnOptions = {}): ImageData {
  const { width, height } = rawBefore;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    if (freshOnly && nbrAt(rawBefore.bands, i) < ALREADY_BURNED_NBR) continue;
    const d = bais2At(rawAfter.bands, i) - bais2At(rawBefore.bands, i);
    const t = Math.min(Math.max((d - 0.12) / 0.55, 0), 1);
    if (t <= 0) continue;
    // amber -> crimson ramp
    out[o] = 255 - Math.round(60 * t);
    out[o + 1] = Math.round(140 * (1 - t));
    out[o + 2] = Math.round(40 * t);
    out[o + 3] = Math.round((0.4 + 0.55 * t) * 255);
  }
  return new ImageData(out, width, height);
}

// Water & wetness change via dMNDWI. This is the layer for the question the
// generic difference cannot answer: "is that patch damage, or did it just get
// wet?" MNDWI (green vs SWIR1) swings hard positive when a surface wets or
// floods and negative as it dries, while rubble/char move it little — so the
// blue end here is exactly the change the damage layers should be doubted on.
// Diverging on purpose: receding water is as informative as arriving water.
export function compositeWaterChange(rawBefore: RawScene, rawAfter: RawScene,
                                     { normalize = true, normStrength = 1 } = {}): ImageData {
  const { width, height } = rawBefore;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const fits = normalize
    ? scaleFits(radiometricFit(rawBefore, rawAfter, ['B03', 'B11']), normStrength) : null;
  const gG = fits ? fits.B03!.gain : 1, oG = fits ? fits.B03!.offset : 0;
  const gS = fits ? fits.B11!.gain : 1, oS = fits ? fits.B11!.offset : 0;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    const greenB = rawBefore.bands.B03![i]!, swirB = rawBefore.bands.B11![i]!;
    const greenA = Math.max(0, rawAfter.bands.B03![i]! * gG + oG);
    const swirA = Math.max(0, rawAfter.bands.B11![i]! * gS + oS);
    const d = (greenA - swirA) / (greenA + swirA + 1)
            - (greenB - swirB) / (greenB + swirB + 1);
    if (Math.abs(d) < 0.08) continue;
    const t = Math.min(1, (Math.abs(d) - 0.08) / 0.45);
    const c = d > 0 ? lerpColor([143, 208, 232], [28, 80, 168], t)   // wetter: pale -> deep blue
                    : lerpColor([214, 178, 120], [150, 92, 40], t);  // drier: tan -> ochre
    out[o] = c[0]!; out[o + 1] = c[1]!; out[o + 2] = c[2]!;
    out[o + 3] = Math.round((0.35 + 0.6 * t) * 255);
  }
  return new ImageData(out, width, height);
}

// Active fire / hotspot detection on the AFTER scene only. Burning ground
// radiates strongly at 2.2 µm: B12 spikes far above B11 and B8A (the basis of
// published Sentinel-2 active-fire detectors). Where B12 saturates the pixel
// renders white-hot.
export function compositeHotspot(rawAfter: RawScene): ImageData {
  const { width, height, bands } = rawAfter;
  const mask = bands.dataMask!;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (mask[i]! <= SCALE / 2) continue;
    const r12 = bands.B12![i]! / SCALE;
    const r11 = bands.B11![i]! / SCALE;
    const r8a = bands.B8A![i]! / SCALE;
    const hot = Math.min(Math.max((r12 - 0.35) / 0.4, 0), 1);             // absolute SWIR2 brightness
    const spectral = Math.min(Math.max((r12 - r11 + 0.02) / 0.18, 0), 1); // fire's 2.2µm slope vs bare bright ground
    const t = hot * spectral;
    if (t <= 0.02) continue;
    if (r12 > 0.9 && r11 > 0.55 && r12 >= r11 && r11 > r8a) {
      out[o] = 255; out[o + 1] = 250; out[o + 2] = 230; out[o + 3] = 255; // saturated: white-hot core
    } else {
      out[o] = 255;
      out[o + 1] = Math.round(90 + 120 * (1 - t));
      out[o + 2] = 0;
      out[o + 3] = Math.round((0.45 + 0.55 * t) * 255);
    }
  }
  return new ImageData(out, width, height);
}

// EXPERIMENTAL: fire-retardant and firefighting-foam residue. Long-term
// retardant (e.g. Phos-Chek) is iron-oxide pigmented — dropping it shifts the
// ground's visible spectrum sharply toward red; foam/white residue raises
// visible albedo across all three bands with little redness shift. Both are
// computed as BEFORE->AFTER shifts (radiometrically matched), so a place that
// was always red/bright doesn't flag. Pink = retardant-like, cyan = foam-like.
export function compositeRetardant(rawBefore: RawScene, rawAfter: RawScene,
                                   { normalize = true, normStrength = 1 } = {}): ImageData {
  const VIS = ['B02', 'B03', 'B04'];
  const { width, height } = rawBefore;
  const maskB = rawBefore.bands.dataMask!;
  const maskA = rawAfter.bands.dataMask!;
  const fits = normalize
    ? scaleFits(radiometricFit(rawBefore, rawAfter, [...VIS, 'B08']), normStrength) : null;
  const out = new Uint8ClampedArray(width * height * 4);

  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (maskB[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    const b2b = rawBefore.bands.B02![i]! / SCALE;
    const b3b = rawBefore.bands.B03![i]! / SCALE;
    const b4b = rawBefore.bands.B04![i]! / SCALE;
    const b2a = (fits ? rawAfter.bands.B02![i]! * fits.B02!.gain + fits.B02!.offset
                      : rawAfter.bands.B02![i]!) / SCALE;
    const b3a = (fits ? rawAfter.bands.B03![i]! * fits.B03!.gain + fits.B03!.offset
                      : rawAfter.bands.B03![i]!) / SCALE;
    const b4a = (fits ? rawAfter.bands.B04![i]! * fits.B04!.gain + fits.B04!.offset
                      : rawAfter.bands.B04![i]!) / SCALE;
    const nirB = rawBefore.bands.B08![i]! / SCALE;
    const nirA = (fits ? rawAfter.bands.B08![i]! * fits.B08!.gain + fits.B08!.offset
                       : rawAfter.bands.B08![i]!) / SCALE;

    const sumB = b2b + b3b + b4b + 1e-6, sumA = b2a + b3a + b4a + 1e-6;
    const rednessShift = b4a / sumA - b4b / sumB;         // toward iron-oxide red
    const brightShift = (sumA - sumB) / 3;                // whole-visible albedo rise
    const nirDrop = nirB - nirA;                          // retardant blankets vegetation

    // Retardant: strong redness shift onto a now-red surface, usually with a
    // NIR drop. Foam/white residue: broad brightening without the red shift.
    const retardant = Math.min(Math.max((rednessShift - 0.025) / 0.06, 0), 1) *
      Math.min(Math.max((b4a / sumA - 0.36) / 0.08, 0), 1) *
      (0.6 + 0.4 * Math.min(Math.max(nirDrop / 0.08, 0), 1));
    const foam = Math.min(Math.max((brightShift - 0.06) / 0.12, 0), 1) *
      (1 - Math.min(Math.max(rednessShift / 0.03, 0), 1));

    if (retardant > 0.05 && retardant >= foam) {
      out[o] = 255; out[o + 1] = 96; out[o + 2] = 196;
      out[o + 3] = Math.round((0.35 + 0.65 * retardant) * 255);
    } else if (foam > 0.05) {
      out[o] = 178; out[o + 1] = 247; out[o + 2] = 255;
      out[o + 3] = Math.round((0.35 + 0.65 * foam) * 255);
    }
  }
  return new ImageData(out, width, height);
}

// ---------------------------------------------------------------------------
// Deep analysis: a multi-scene baseline instead of a single before date.
//
// A two-date comparison cannot tell "this pixel changed" from "this pixel
// always varies" — water glints, wet season vs dry, parking lots, sun angle.
// Stacking several co-registered, radiometrically-fitted scenes gives every
// pixel a per-band MEDIAN (what this ground normally looks like) and a robust
// SPREAD (how much it normally wanders). The after scene is then judged
// against that envelope: inside it is business as usual no matter how visually
// different it looks; outside it is change the area has NOT shown before.
//
// The median stack is also its own reward: co-registered medians average away
// sensor noise and one-off contaminations (a cloud shadow, a smoke plume in
// one scene), so the "before" the operator compares against is cleaner than
// any single acquisition — a little extra detail squeezed out of the same
// 10 m pixels.

export interface BaselineInput { raw: RawScene; fits: BandFits | null }

export interface BaselineStats {
  width: number;
  height: number;
  /** How many baseline scenes were valid at each pixel. */
  count: Uint8Array;
  /** SCALE where count >= minCount, else 0 — the stack's dataMask. */
  dataMask: Uint16Array;
  median: Record<string, Uint16Array>;
  spread: Record<string, Float32Array>;
}

// The spread floor, in DN (~0.9% reflectance). With 3-8 samples the MAD is a
// coarse estimate and a run of near-identical values would otherwise declare
// a pixel perfectly stable, making any noise-level wiggle "anomalous".
export const BASELINE_SIGMA_FLOOR = 90;

export function baselineCounts(scenes: BaselineInput[], minCount = 3):
    { count: Uint8Array; dataMask: Uint16Array } {
  const { width, height } = scenes[0]!.raw;
  const n = width * height;
  const count = new Uint8Array(n);
  for (const { raw } of scenes) {
    const mask = raw.bands.dataMask!;
    for (let i = 0; i < n; i++) if (mask[i]! > SCALE / 2) count[i]!++;
  }
  const dataMask = new Uint16Array(n);
  for (let i = 0; i < n; i++) if (count[i]! >= minCount) dataMask[i] = SCALE;
  return { count, dataMask };
}

// One band's median + robust spread across the stack. Exposed per band (rather
// than only via buildBaselineStats) so the Studio can yield to the event loop
// between bands — the full stack pass is seconds of arithmetic and must not
// freeze the page in one block.
export function baselineBandStat(scenes: BaselineInput[], name: string,
                                 dataMask: Uint16Array):
    { median: Uint16Array; spread: Float32Array } {
  const n = dataMask.length;
  const median = new Uint16Array(n);
  const spread = new Float32Array(n);
  const vals = new Float64Array(scenes.length);
  for (let i = 0; i < n; i++) {
    if (dataMask[i]! <= SCALE / 2) continue;
    let k = 0;
    for (const { raw, fits } of scenes) {
      if (raw.bands.dataMask![i]! <= SCALE / 2) continue;
      const fit = fits?.[name];
      const v = raw.bands[name]![i]!;
      // Insertion sort as we gather — k is at most 8.
      let value = fit ? Math.max(0, v * fit.gain + fit.offset) : v;
      let j = k++;
      while (j > 0 && vals[j - 1]! > value) { vals[j] = vals[j - 1]!; j--; }
      vals[j] = value;
    }
    const med = k % 2 ? vals[(k - 1) >> 1]! : (vals[(k >> 1) - 1]! + vals[k >> 1]!) / 2;
    median[i] = Math.round(Math.min(65535, Math.max(0, med)));
    // MAD -> sigma (1.4826 for a normal population), floored: see above.
    let maxDev = 0;
    for (let m = 0; m < k; m++) {
      const dev = Math.abs(vals[m]! - med);
      vals[m] = dev;
      if (dev > maxDev) maxDev = dev;
    }
    let j = 1;
    for (; j < k; j++) {           // re-sort the deviations (still tiny)
      const value = vals[j]!;
      let p = j;
      while (p > 0 && vals[p - 1]! > value) { vals[p] = vals[p - 1]!; p--; }
      vals[p] = value;
    }
    const mad = k % 2 ? vals[(k - 1) >> 1]! : (vals[(k >> 1) - 1]! + vals[k >> 1]!) / 2;
    spread[i] = Math.max(BASELINE_SIGMA_FLOOR, 1.4826 * mad);
  }
  return { median, spread };
}

/** Convenience (and the testable whole): counts + every band in one call. */
export function buildBaselineStats(scenes: BaselineInput[], bandNames = RAW_BANDS,
                                   minCount = 3): BaselineStats {
  const { width, height } = scenes[0]!.raw;
  const { count, dataMask } = baselineCounts(scenes, minCount);
  const median: Record<string, Uint16Array> = {};
  const spread: Record<string, Float32Array> = {};
  for (const name of bandNames) {
    const stat = baselineBandStat(scenes, name, dataMask);
    median[name] = stat.median;
    spread[name] = stat.spread;
  }
  return { width, height, count, dataMask, median, spread };
}

/** The median stack as a scene, so every existing style/composite path — and
 *  compositeScenePair's shared-stretch pair rendering — works on it unchanged. */
export function baselineScene(stats: BaselineStats): RawScene {
  const bands: RawScene['bands'] = { dataMask: stats.dataMask };
  for (const [name, arr] of Object.entries(stats.median)) bands[name] = arr;
  return { width: stats.width, height: stats.height, bands };
}

export interface AnomalyOptions {
  bandSet: 'core' | 'extended';
  sensitivity: number;
  despeckle?: boolean;
  ignoreWater?: boolean;
  ignoreRecovery?: boolean;
}

// Score the after scene against the baseline envelope. Per band the deviation
// from the median is expressed in units of that pixel's own spread (a robust
// z), combined by RMS across the band set, and mapped so the visible 0.15
// threshold sits exactly at the z threshold the sensitivity dial picked —
// below it a faint sub-threshold trace, above it a ramp that saturates at 4x.
// Pixels the baseline cannot vouch for (fewer than minCount valid scenes)
// stay ungraded rather than pretending an answer.
export function computeBaselineAnomaly(stats: BaselineStats, rawAfter: RawScene,
                                       fitsAfter: BandFits | null,
                                       { bandSet, sensitivity, despeckle = false,
                                         ignoreWater = false, ignoreRecovery = false,
                                       }: AnomalyOptions): ChangeResult {
  const bandsList = bandSet === 'core' ? CORE_BANDS : EXTENDED_BANDS;
  const s = Math.min(10, Math.max(1, sensitivity));
  const zThr = 6.5 - 0.5 * s;   // s=1 -> 6 sigma (calm), s=10 -> 1.5 sigma (hot)
  const { width, height } = stats;
  const maskA = rawAfter.bands.dataMask!;
  const gating = (ignoreWater || ignoreRecovery)
    && !!(stats.median.B03 && stats.median.B08 && stats.median.B11 && stats.median.B12
          && rawAfter.bands.B03);
  const gateFits = fitsAfter ?? {};
  const medians = bandsList.map((name) => stats.median[name]!);
  const spreads = bandsList.map((name) => stats.spread[name]!);
  const afters = bandsList.map((name) => rawAfter.bands[name]!);
  const gains = bandsList.map((name) => fitsAfter?.[name]?.gain ?? 1);
  const offsets = bandsList.map((name) => fitsAfter?.[name]?.offset ?? 0);

  let score = new Float32Array(width * height);
  const valid = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    if (stats.dataMask[i]! <= SCALE / 2 || maskA[i]! <= SCALE / 2) continue;
    valid[i] = 1;
    let sumSq = 0;
    for (let k = 0; k < bandsList.length; k++) {
      const a = Math.max(0, afters[k]![i]! * gains[k]! + offsets[k]!);
      const z = Math.abs(a - medians[k]![i]!) / spreads[k]![i]!;
      sumSq += z * z;
    }
    const z = Math.sqrt(sumSq / bandsList.length);
    let t = z < zThr ? 0.14 * (z / zThr)
                     : 0.15 + 0.85 * Math.min(1, (z - zThr) / (3 * zThr));
    if (t > 0.02 && gating
        && gateSuppresses(stats.median, rawAfter.bands, gateFits, i,
                          ignoreWater, ignoreRecovery)) {
      t = 0;
    }
    score[i] = t;
  }
  if (despeckle) score = boxSmooth3(score, width, height);

  const out = new Uint8ClampedArray(width * height * 4);
  let mask = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    if (!valid[i]) continue;
    const o = i * 4;
    const [r, g, b, a] = ramp(score[i]!);
    out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
    if (score[i]! >= 0.15) mask[i] = 1;
  }
  if (despeckle) {
    mask = removeSmallComponents(mask, width, height, 3);
    for (let i = 0; i < mask.length; i++) if (!mask[i]) out[i * 4 + 3] = 0;
  }
  return { imageData: new ImageData(out, width, height), mask, score, valid };
}

// Light unsharp sharpening (3x3 kernel 0,-1,0 / -1,5,-1 / 0,-1,0 — the same
// kernel the old app's SVG filter used). Applied to composed RGB, not raw DN.
/** RGBA pixels (a decoded PNG) as a RawScene: R/G/B on the reflectance SCALE
 *  plus a dataMask. No-data is transparent OR pure black: GIBS paints a day or
 *  swath it has no granule for as opaque (0,0,0) — measured 2026-09-26 on a
 *  day whose pass hadn't flown yet — so black is the only mark it leaves. */
export function rgbaToScene(data: Uint8ClampedArray, width: number,
                            height: number, blackIsNoData = true): RawScene {
  const n = width * height;
  const r = new Uint16Array(n), g = new Uint16Array(n), b = new Uint16Array(n);
  const mask = new Uint16Array(n);
  const k = SCALE / 255;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    if (data[o + 3]! === 0) continue;
    if (blackIsNoData && (data[o]! | data[o + 1]! | data[o + 2]!) === 0) continue;
    r[i] = Math.round(data[o]! * k);
    g[i] = Math.round(data[o + 1]! * k);
    b[i] = Math.round(data[o + 2]! * k);
    mask[i] = SCALE;
  }
  return { width, height, bands: { R: r, G: g, B: b, dataMask: mask } };
}

/** An RGB scene drawn exactly as delivered — no stretch, no fit, no tone map.
 *  For the NOAA VIIRS images, which arrive already rendered: any stretch here
 *  would be a second edit of someone else's picture. */
export function compositeRgb(raw: RawScene): ImageData {
  const { width, height, bands } = raw;
  const r = bands.R!, g = bands.G!, b = bands.B!, mask = bands.dataMask!;
  const out = new Uint8ClampedArray(width * height * 4);
  const k = 255 / SCALE;
  for (let i = 0; i < width * height; i++) {
    if (mask[i]! <= SCALE / 2) continue;
    const o = i * 4;
    out[o] = Math.round(r[i]! * k);
    out[o + 1] = Math.round(g[i]! * k);
    out[o + 2] = Math.round(b[i]! * k);
    out[o + 3] = 255;
  }
  return new ImageData(out, width, height);
}

/** Share of a scene's pixels that carry data. */
export function validShare(raw: RawScene): number {
  const mask = raw.bands.dataMask!;
  let n = 0;
  for (let i = 0; i < mask.length; i++) if (mask[i]! > SCALE / 2) n++;
  return mask.length ? n / mask.length : 0;
}

export function sharpenImage(img: ImageData): ImageData {
  const { width, height, data } = img;
  const out = new Uint8ClampedArray(data);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = (y * width + x) * 4;
      if (data[i + 3] === 0) continue;
      for (let c = 0; c < 3; c++) {
        const v = 5 * data[i + c]!
          - data[i - 4 + c]! - data[i + 4 + c]!
          - data[i - width * 4 + c]! - data[i + width * 4 + c]!;
        out[i + c] = v;
      }
    }
  }
  return new ImageData(out, width, height);
}

export function imageDataToCanvas(img: ImageData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  canvas.getContext('2d')!.putImageData(img, 0, 0);
  return canvas;
}
